import type {
  ContextByTypeAndId,
  TreeHierarchyCheck,
  TreeHierarchyContext,
  TreeHierarchyWorkflow,
  TreeHierarchyItem,
} from '../types'
import {
  CheckContext,
  CheckPayload,
  SilverfinContextCamelCase,
  WorkflowListItem,
} from '@app/shared/types/sf-data'

import omit from 'lodash/omit'

import TreeNode from '@app/utils/tree'

export const collectReadStatusById = (
  checksResponse: CheckPayload
): Record<string, boolean> => {
  return checksResponse.readStatus.reduce((acc, checkId) => {
    acc[checkId] = true
    return acc
  }, {} as Record<string, boolean>)
}

export function generateChecksTreeHierarchy({
  checksResponse,
  workflowId,
  workflows = [],
}: {
  checksResponse: CheckPayload | undefined
  workflowId: number | null
  workflows: WorkflowListItem[] | undefined
}): TreeNode<TreeHierarchyItem> {
  if (!checksResponse) {
    return TreeNode.of<TreeHierarchyItem>(
      {
        id: '1',
        name: 'root',
        type: 'group',
        children: [],
      },
      []
    )
  }

  const contextByTypeById = getContextByTypeAndId(checksResponse.contexts)

  const workflowsToReduce: WorkflowListItem[] = workflowId
    ? workflows.filter((workflow) => workflow.id === workflowId)
    : [
        ...workflows,
        {
          id: 1,
          default: false,
          name: 'General',
        } as WorkflowListItem,
      ]

  const treeHierarchyStructure = workflowsToReduce
    .reduce((workflowAccumulator, workflow) => {
      if (workflow.name === 'General') {
        // Insert a dummy top-level node for the general category
        workflowAccumulator.push({
          ...workflow,
          id: 'general',
          type: 'workflow',
          children: checksResponse.general.map((check) =>
            Object.freeze({
              ...check,
              contextUrl: '',
              contextUrlText: '',
              type: 'check',
            } as TreeHierarchyCheck)
          ),
        })
      } else {
        workflowAccumulator.push(
          Object.freeze({
            ...omit(workflow, ['accountIds', 'reconciliationTextIds']),
            id: workflow.id.toString(),
            type: 'workflow',
            children: [
              ...Object.keys(checksResponse.reconciliations).reduce(
                accumulateContext({
                  checksResponse,
                  contextByTypeById,
                  contextType: SilverfinContextCamelCase.ReconciliationText,
                  workflow,
                }),
                [] as TreeHierarchyContext[]
              ),
              ...Object.keys(checksResponse.accounts).reduce(
                accumulateContext({
                  checksResponse,
                  contextByTypeById,
                  contextType: SilverfinContextCamelCase.Account,
                  workflow,
                }),
                [] as TreeHierarchyContext[]
              ),
            ].sort(contextSorter),
          })
        )
      }

      return workflowAccumulator
    }, [] as TreeHierarchyWorkflow[])
    .sort(workflowSorter)

  const tree = TreeNode.of<TreeHierarchyItem>(
    {
      id: '1',
      name: 'root',
      type: 'group',
      children: [],
    },
    treeHierarchyStructure
  )

  return tree
}

const getContextByTypeAndId = (
  contexts: CheckContext[]
): ContextByTypeAndId => {
  return contexts.reduce(
    (acc, context) => {
      if (context.type === SilverfinContextCamelCase.Account) {
        acc.accounts[context.id] = context
      } else {
        acc.reconciliations[context.id] = context
      }

      return acc
    },
    {
      accounts: {} as Record<number, CheckContext>,
      reconciliations: {} as Record<number, CheckContext>,
    }
  )
}

const accumulateContext =
  ({
    checksResponse,
    contextByTypeById,
    contextType,
    workflow,
  }: {
    checksResponse: CheckPayload
    contextByTypeById: ContextByTypeAndId
    contextType: SilverfinContextCamelCase
    workflow: WorkflowListItem
  }) =>
  (
    accumulator: TreeHierarchyContext[],
    contextIdString: string
  ): TreeHierarchyContext[] => {
    const numberContextId = parseInt(contextIdString, 10)

    const existsForContextType =
      contextType === SilverfinContextCamelCase.Account
        ? workflow.accountIds.includes(numberContextId)
        : workflow.reconciliationTextIds.includes(numberContextId)

    if (!existsForContextType) {
      return accumulator
    }

    const context =
      contextByTypeById[
        contextType === SilverfinContextCamelCase.Account
          ? 'accounts'
          : 'reconciliations'
      ][numberContextId]

    if (!context) {
      return accumulator
    }

    const contextualChecks =
      contextType === SilverfinContextCamelCase.Account
        ? checksResponse.accounts[numberContextId]
        : checksResponse.reconciliations[numberContextId]

    accumulator.push({
      ...context,
      id: getComplexContextIdByTypeAndId(context.type, context.id),
      children: contextualChecks.map((check) =>
        Object.freeze({
          ...check,
          contextUrl: context.url,
          contextUrlText: context.name,
          type: 'check',
        } as TreeHierarchyCheck)
      ),
    })

    return accumulator
  }

function getComplexContextIdByTypeAndId(
  type:
    | SilverfinContextCamelCase.Account
    | SilverfinContextCamelCase.ReconciliationText,
  contextId: number
): string {
  if (type === SilverfinContextCamelCase.Account) {
    return `account__${contextId}`
  } else if (type === SilverfinContextCamelCase.ReconciliationText) {
    return `reconciliation__${contextId}`
  }

  return `${contextId}`
}

const contextSorter = (
  a: TreeHierarchyContext,
  b: TreeHierarchyContext
): number => a.name.localeCompare(b.name)

const workflowSorter = (
  a: TreeHierarchyWorkflow,
  b: TreeHierarchyWorkflow
): number => {
  if (a.default) {
    return -1
  } else if (b.default) {
    return 1
  }
  return a.name.localeCompare(b.name)
}
